import axios from 'axios'
import { UserProfile, RecommendationResponse, ApiResponse, RunningShoe, EnhancedUserProfile, EnhancedRecommendationResponse } from '../types'

// Create axios instance with base configuration
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:5001/api',
  timeout: 30000, // 30 seconds for AI processing
  headers: {
    'Content-Type': 'application/json',
  },
})

// Add request interceptor to include session ID and auth token
api.interceptors.request.use((config) => {
  let sessionId = sessionStorage.getItem('sessionId')
  
  // In development mode, ensure we have a sessionId
  if (!sessionId && (import.meta.env.DEV || window.location.hostname === 'localhost')) {
    sessionId = 'dev-session-bx23r'
    sessionStorage.setItem('sessionId', sessionId)
  }
  
  if (sessionId) {
    config.headers['X-Session-ID'] = sessionId
  }
  
  // Add auth token if available
  const authToken = localStorage.getItem('auth_token')
  if (authToken) {
    config.headers['Authorization'] = `Bearer ${authToken}`
  }
  
  return config
})

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error.response?.data || error.message)
    
    if (error.response?.status === 429) {
      throw new Error('Too many requests. Please wait a moment and try again.')
    }
    
    if (error.response?.status === 503 && error.response?.data?.error === 'Database service unavailable') {
      // Create a custom error that preserves the original response
      const customError = new Error(error.response.data.message || 'Database temporarily unavailable. Please try again later.')
      ;(customError as any).response = error.response
      throw customError
    }
    
    if (error.response?.status >= 500) {
      throw new Error('Server error. Please try again later.')
    }
    
    if (error.code === 'ECONNABORTED') {
      throw new Error('Request timed out. Please try again.')
    }
    
    throw error
  }
)

/**
 * Get shoe recommendations based on user profile
 */
export const getRecommendations = async (
  userProfile: UserProfile,
  sessionId: string
): Promise<RecommendationResponse> => {
  try {
    const response = await api.post<RecommendationResponse>('/recommendations', userProfile, {
      headers: {
        'X-Session-ID': sessionId,
      },
    })
    
    return response.data
  } catch (error: any) {
    if (error.response?.data) {
      return {
        success: false,
        error: error.response.data.error || 'Failed to get recommendations',
        details: error.response.data.details,
      } as any
    }
    
    return {
      success: false,
      error: error.message || 'Network error occurred',
    } as any
  }
}

/**
 * Get recommendation history for a session
 */
export const getRecommendationHistory = async (
  page: number = 1,
  limit: number = 10
): Promise<ApiResponse<any[]>> => {
  try {
    const response = await api.get('/recommendations/history', {
      params: { page, limit },
    })
    
    return response.data
  } catch (error: any) {
    throw new Error(error.response?.data?.error || 'Failed to get recommendation history')
  }
}

/**
 * Submit feedback for a recommendation
 */
export const submitFeedback = async (
  recommendationId: string,
  feedback: {
    rating: number
    comments?: string
    selectedShoe?: string
    purchaseIntention?: 'definitely' | 'probably' | 'maybe' | 'unlikely'
  }
): Promise<ApiResponse<void>> => {
  try {
    const response = await api.post(`/recommendations/${recommendationId}/feedback`, feedback)
    return response.data
  } catch (error: any) {
    throw new Error(error.response?.data?.error || 'Failed to submit feedback')
  }
}

/**
 * Get all shoes with filtering
 */
export const getShoes = async (params: {
  page?: number
  limit?: number
  gender?: string
  brand?: string
  minPrice?: number
  maxPrice?: number
  size?: number
  terrain?: string
  inStock?: boolean
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
  search?: string
}): Promise<ApiResponse<{
  data: RunningShoe[]
  pagination: {
    page: number
    limit: number
    total: number
    pages: number
  }
  filterOptions: any
}>> => {
  try {
    const response = await api.get('/shoes', { params })
    return response.data
  } catch (error: any) {
    throw new Error(error.response?.data?.error || 'Failed to get shoes')
  }
}

/**
 * Get a specific shoe by ID
 */
export const getShoeById = async (id: string): Promise<ApiResponse<{
  shoe: RunningShoe
  similarShoes: RunningShoe[]
}>> => {
  try {
    const response = await api.get(`/shoes/${id}`)
    return response.data
  } catch (error: any) {
    throw new Error(error.response?.data?.error || 'Failed to get shoe details')
  }
}

/**
 * Get price history for a specific shoe
 */
export const getPriceHistory = async (
  id: string,
  period: '15d' | '1m' | '3m' | '1y' = '1m',
  gender: 'men' | 'women' = 'men'
): Promise<ApiResponse<Array<{
  date: Date
  price: number
  originalPrice: number
}>>> => {
  try {
    const response = await api.get(`/shoes/${id}/price-history`, {
      params: { period, gender }
    })
    return response.data
  } catch (error: any) {
    throw new Error(error.response?.data?.error || 'Failed to get price history')
  }
}

/**
 * Get featured shoes
 */
export const getFeaturedShoes = async (
  type: 'deals' | 'newest' | 'popular' | 'budget' = 'deals',
  limit: number = 8
): Promise<ApiResponse<RunningShoe[]>> => {
  try {
    const response = await api.get('/shoes/featured', {
      params: { type, limit },
    })
    return response.data
  } catch (error: any) {
    throw new Error(error.response?.data?.error || 'Failed to get featured shoes')
  }
}

/**
 * Search shoes with autocomplete
 * API returns: {success: true, data: RunningShoe[], suggestions: string[], query: string}
 */
export const searchShoes = async (
  query: string,
  limit: number = 10
): Promise<{
  success: boolean
  data: RunningShoe[]
  suggestions: string[]
  query: string
}> => {
  try {
    const response = await api.get('/shoes/search', {
      params: { q: query, limit },
    })
    return response.data
  } catch (error: any) {
    throw new Error(error.response?.data?.error || 'Failed to search shoes')
  }
}

/**
 * Get enhanced recommendations with AI reasoning
 */
export const getEnhancedRecommendations = async (
  userProfile: EnhancedUserProfile
): Promise<EnhancedRecommendationResponse> => {
  try {
    const response = await api.post<EnhancedRecommendationResponse>('/recommendations/enhanced', userProfile)
    return response.data
  } catch (error: any) {
    // Preserve the original error response for proper handling in components
    if (error.response?.data) {
      const customError = new Error(error.response.data.error || 'Failed to get enhanced recommendations') as any
      customError.response = error.response
      throw customError
    }
    throw new Error(error.message || 'Network error occurred')
  }
}

/**
 * Get API health status
 */
export const getHealthStatus = async (): Promise<ApiResponse<any>> => {
  try {
    const response = await api.get('/health')
    return response.data
  } catch (error: any) {
    throw new Error('API health check failed')
  }
}

/**
 * Get AI service status
 */
export const getAIStatus = async (): Promise<ApiResponse<any>> => {
  try {
    const response = await api.get('/ai/status')
    return response.data
  } catch (error: any) {
    throw new Error('Failed to get AI status')
  }
}

/**
 * Trigger manual scraping (admin function)
 */
export const triggerScraping = async (): Promise<ApiResponse<void>> => {
  try {
    const response = await api.post('/scraping/trigger')
    return response.data
  } catch (error: any) {
    throw new Error(error.response?.data?.error || 'Failed to trigger scraping')
  }
}

/**
 * Get scraping status
 */
export const getScrapingStatus = async (): Promise<ApiResponse<any>> => {
  try {
    const response = await api.get('/scraping/status')
    return response.data
  } catch (error: any) {
    throw new Error('Failed to get scraping status')
  }
}

/**
 * Check Buy Now permission
 */
export const checkBuyNowPermission = async (): Promise<{
  success: boolean;
  hasPermission: boolean;
  needsAuth: boolean;
  needsPayment: boolean;
  message?: string;
  premiumAccess?: {
    expiryDate: string;
    daysRemaining: number;
  };
}> => {
  try {
    // Add cache-busting parameter to prevent browser caching
    const cacheBuster = Date.now()
    const response = await api.get(`/auth/buy-now-permission?_t=${cacheBuster}`, {
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      }
    })
    return response.data
  } catch (error: any) {
    throw new Error(error.response?.data?.error || 'Failed to check Buy Now permission')
  }
}

// ============ FAVORITES API ============

export interface FavoriteShoe {
  _id: string;
  userId: string;
  shoeId: string;
  shoeName: string;
  shoeBrand: string;
  shoeImage: string;
  shoeUrl: string;
  currentPrice?: number;
  originalPrice?: number;
  discountPercentage?: number;
  priceAlert?: {
    targetPrice: number;
    isActive: boolean;
  };
  createdAt: string;
  updatedAt: string;
}

/**
 * Get user's favorite shoes
 */
export const getFavorites = async (): Promise<ApiResponse<{ favorites: FavoriteShoe[] }>> => {
  try {
    const response = await api.get('/favorites')
    return response.data
  } catch (error: any) {
    throw new Error(error.response?.data?.error || 'Failed to get favorites')
  }
}

/**
 * Add shoe to favorites
 */
export const addFavorite = async (shoeData: {
  shoeId: string;
  shoeName: string;
  shoeBrand: string;
  shoeImage: string;
  shoeUrl: string;
  currentPrice?: number;
  originalPrice?: number;
  discountPercentage?: number;
}): Promise<ApiResponse<{ favorite: FavoriteShoe }>> => {
  try {
    const response = await api.post('/favorites', shoeData)
    return response.data
  } catch (error: any) {
    // Check if it's a database unavailable error
    if (error.response?.status === 503 && error.response?.data?.message) {
      throw new Error(error.response.data.message)
    }
    throw new Error(error.response?.data?.error || 'Failed to add favorite')
  }
}

/**
 * Remove shoe from favorites
 */
export const removeFavorite = async (shoeId: string): Promise<ApiResponse<void>> => {
  try {
    const response = await api.delete(`/favorites/${shoeId}`)
    return response.data
  } catch (error: any) {
    throw new Error(error.response?.data?.error || 'Failed to remove favorite')
  }
}

/**
 * Check if shoe is favorited
 */
export const checkFavoriteStatus = async (shoeId: string): Promise<{ isFavorite: boolean }> => {
  try {
    const response = await api.get(`/favorites/check/${shoeId}`)
    return response.data.data || response.data
  } catch (error: any) {
    // Return false if not authenticated, database unavailable, or any error occurs
    if (error.response?.status === 503) {
      console.log('Favorites feature unavailable: database connection needed')
    }
    return { isFavorite: false }
  }
}

/**
 * Set price alert for favorite shoe
 */
export const setPriceAlert = async (shoeId: string, targetPrice: number): Promise<ApiResponse<{ favorite: FavoriteShoe }>> => {
  try {
    const response = await api.post(`/favorites/${shoeId}/price-alert`, { targetPrice })
    return response.data
  } catch (error: any) {
    throw new Error(error.response?.data?.error || 'Failed to set price alert')
  }
}

/**
 * Remove price alert for favorite shoe
 */
export const removePriceAlert = async (shoeId: string): Promise<ApiResponse<{ favorite: FavoriteShoe }>> => {
  try {
    const response = await api.delete(`/favorites/${shoeId}/price-alert`)
    return response.data
  } catch (error: any) {
    throw new Error(error.response?.data?.error || 'Failed to remove price alert')
  }
}

// Change password (2025-10-10: Real API integration)
export const changePassword = async (currentPassword: string, newPassword: string) => {
  const response = await api.post('/auth/change-password', {
    currentPassword,
    newPassword
  })
  return response.data
}

// Email preferences API (2025-10-10: Price alert toggle integration)
export const getEmailPreferences = async () => {
  const response = await api.get('/user-preferences/email-preferences')
  return response.data
}

export const updateEmailPreferences = async (priceAlertEnabled: boolean, priceDropThreshold: number) => {
  const response = await api.put('/user-preferences/email-preferences', {
    priceAlertEnabled,
    priceDropThreshold
  })
  return response.data
}

/**
 * Submit customer feedback
 */
export const submitCustomerFeedback = async (formData: FormData): Promise<ApiResponse<void>> => {
  const response = await api.post('/feedback', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
}

// ============ PAYMENT API ============

/**
 * Create Stripe Checkout Session
 */
export const createCheckoutSession = async (shoeId?: string): Promise<{
  success: boolean;
  data?: {
    sessionId: string;
    url: string;
  };
  message?: string;
}> => {
  try {
    const response = await api.post('/payment/create-checkout-session', {
      shoeId,
      returnUrl: window.location.href
    });
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message: error.response?.data?.message || error.message || 'Failed to create checkout session'
    };
  }
}

/**
 * Get subscription status
 */
export const getSubscriptionStatus = async (): Promise<{
  success: boolean;
  data?: {
    isPremium: boolean;
    expiryDate: string | null;
    daysRemaining: number;
    subscriptionId: string | null;
    paymentMethod: string | null;
  };
  message?: string;
}> => {
  try {
    const response = await api.get('/payment/subscription-status');
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message: error.response?.data?.message || error.message || 'Failed to get subscription status'
    };
  }
}

/**
 * Cancel subscription
 */
export const cancelSubscription = async (): Promise<{
  success: boolean;
  data?: {
    cancelAt: string;
  };
  message?: string;
}> => {
  try {
    const response = await api.post('/payment/cancel-subscription');
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message: error.response?.data?.message || error.message || 'Failed to cancel subscription'
    };
  }
}

/**
 * Verify payment success
 */
export const verifyPaymentSuccess = async (sessionId: string): Promise<{
  success: boolean;
  data?: {
    isPremium: boolean;
    expiryDate?: string;
    daysRemaining?: number;
    pending?: boolean;
  };
  message?: string;
}> => {
  try {
    const response = await api.get(`/payment/verify-success?sessionId=${sessionId}`);
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message: error.response?.data?.message || error.message || 'Failed to verify payment'
    };
  }
}

// Default export with all methods

export default {
  getRecommendations,
  getEnhancedRecommendations,
  getRecommendationHistory,
  submitFeedback,
  getShoes,
  getShoeById,
  getPriceHistory,
  getFeaturedShoes,
  searchShoes,
  getHealthStatus,
  getAIStatus,
  triggerScraping,
  getScrapingStatus,
  checkBuyNowPermission,
  getFavorites,
  addFavorite,
  removeFavorite,
  checkFavoriteStatus,
  setPriceAlert,
  removePriceAlert,
  changePassword,
  getEmailPreferences,
  updateEmailPreferences,
  createCheckoutSession,
  getSubscriptionStatus,
  cancelSubscription,
  verifyPaymentSuccess
}